import React, { FunctionComponent, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap } from 'three';
import { hasEntityTapAction, transitionAdjustedRenderOrder } from '../../utils';
import { IEntityActionState, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IModel3d, IModel3dAnimationState, IModel3dReactProps } from '@r3f-component-data-structure';
import { EntityErrorBoundary } from '@r3f/components/boundaries';
import { ITransitionPhase } from '@r3f/transitions/spec';
import {
	useGetAdjustedMemoSpatialProps,
	useGetSubscribedSceneTransitionCategoryRef,
	useGetSubscribedSceneTransitionPhaseRef,
	useHocPointerDown,
	useOnEntityTestClick,
	useResetEntityTransformsOnPlacement,
} from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { hasTapAction } from './hocUtils';
import { Jute } from '../..';
import { filestore, FsModel3D } from '@/filestore';
import { resolveAssetUrl } from '@/asset-urls';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';

interface IModel3dWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}

const withModel3dBehaviour = (WrappedModel3d: FunctionComponent<IModel3dReactProps>): FunctionComponent<IModel3dWrapped> => {
	const FuncComp: FunctionComponent<IModel3dWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null);
		const model3d = useDataStore((state) => state.componentsById?.[id] as IModel3d) ?? {};
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled) ?? true;
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const isPlacementMode = useSceneStore(({ placementMode }) => placementMode);
		const transitionPhase = useSceneStore((state) => state.transitionPhase[sceneId]);
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);
		const isGrabAndGoActive = useUserStore(({ isGrabNGoActive }) => isGrabNGoActive);

		useOnEntityTestClick({ sceneId, entity: model3d, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer({ object3d: pointerGroupRef, enabled: hasEntityTapAction(model3d), layer: typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1 });

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Get entity values
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (model3d?.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(model3d, screenRelative);
		const { opacity, filestoreId, idleAnimation, idlePoseTime, originalBBox, castShadow = false, receiveShadow = false, billboard } = model3d;

		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: model3d, screenRelative });

		// Resolved defensively: this sits ABOVE the EntityErrorBoundary below, so a throw here would
		// escape it and reach the canvas-wide boundary instead of showing this one entity as broken.
		const model3dUrl = resolveAssetUrl('model', filestoreId, (fsId) => filestore.get<FsModel3D>(fsId).getModelUrl());

		// Model animation specific behaviour
		const rawActionState = useActionStateStore((state) => state.entityActionStateDict?.[id]);

		const animationState = useMemo(() => {
			return Object.keys(rawActionState || {}).reduce((acc, actionCategory) => {
				return { ...acc, ...rawActionState?.[actionCategory as keyof IEntityActionState] };
			}, {} as IModel3dAnimationState);
		}, [rawActionState]);

		useInteraction(
			id,
			'onSelect',
			(e) => {
				onPointerDown(e.intersections);
				pointerIsDownRef.current = false;
			},
			hasTapAction(model3d)
		);

		useLayoutEffect(() => {
			const createInitialStaticGeometryIfNoIdleState = () => {
				if (!model3dUrl) return;
				if (!idleAnimation && typeof idlePoseTime === 'undefined') {
					Jute.staticGeometryWorkerController.generateStaticGeometry(model3dUrl, id);
				}
			};

			if (Jute.isContentLoaded) createInitialStaticGeometryIfNoIdleState();
			else Jute.onContentLoaded(createInitialStaticGeometryIfNoIdleState);
			// Was `[]`. The mesh re-renders correctly after a GLB swap because that path is URL-keyed, but
			// the hit-test geometry generated here was seeded once at mount and never regenerated — so
			// TAP TARGETS WENT STALE after a model swap, still matching the shape of the old model.
			// `model3dUrl` is the value actually passed to the worker, so it is the dependency that
			// matters; the idle-state pair gates whether generation happens at all.
		}, [model3dUrl, id, idleAnimation, idlePoseTime]);

		const isPlayingAnimation = isPlacementMode
			? false
			: transitionPhase === ITransitionPhase.enterFinish && (animationState.isPlayingAnimation || (!!model3d.idleAnimation && typeof model3d.idlePoseTime === 'undefined'));

		return (
			<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} position={zeroTuple3} entityId={id} screenRelative={screenRelative || false}>
				<EntityErrorBoundary forceError={!model3dUrl} renderOrder={renderOrder} scale={scale} rotation={rotation} position={position}>
					<group name={`toggleVisibilityGroup-${id}`}>
						<group ref={pointerGroupRef} onPointerUp={() => (pointerIsDownRef.current = false)}>
							<WrappedModel3d
								id={id}
								renderOrder={renderOrder}
								rotation={rotation}
								position={position}
								animationName={animationState.animationName}
								isPlayingAnimation={isPlayingAnimation}
								animationRepetitions={animationState.animationRepetitions}
								animationRunCounter={animationState.animationRunCounter}
								isClampWhenFinished={animationState.isClampWhenFinished}
								animationTransformGroupPrefix='transformGroup-'
								idleAnimation={idleAnimation}
								idlePoseTime={idlePoseTime}
								model3dUrl={model3dUrl ?? ''}
								scale={scale}
								originalBBox={originalBBox}
								castShadow={shadowsEnabled && castShadow}
								receiveShadow={shadowsEnabled && receiveShadow}
								opacity={opacity}
								raycast={raycast}
								sceneId={sceneId}
								enableBVH={hasTapAction(model3d)}
								onPointerDown={(e) => onPointerDown(e.intersections)}
								staticGeometryWorkerController={Jute.staticGeometryWorkerController}
								bvhWorkerController={Jute.bvhWorkerController}
								billboard={billboard}
								isCameraOrbiting={isGrabAndGoActive}
								rotateNoBillboardOuterGroup={false}
							/>
						</group>
					</group>
				</EntityErrorBoundary>
			</EntityLevelFadeGroup>
		);
	};
	return FuncComp;
};

export default withModel3dBehaviour;
